import { BOSS_MAX_HP } from './corePhases';
import { getCoreAppearance } from './coreAppearance';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { TOTAL_SECTORS } from './runConstants';
import { getMaxHp } from './game';
import { getTotalShardsAcquired } from './shardTotals';
import './RewardCollection.css';
import HealthLossFragments from './HealthLossFragments';
import CoreHealthFill, {
  CoreShieldValue,
  HealthShieldSection,
} from './CoreHealthFill';
import { getPlayerShield } from './playerShield';
import { usesPlainHealthBars } from './healthBarPresentation';

const RunStatus = ({
  game,
  display,
  cinematic,
  healthRef,
  shardsRef,
  reduceMotion,
  hiddenOnMobile = false,
  coreBoss = null,
  boardActive = false,
  hideStats = false,
  inShop = false,
}) => {
  const hp = display?.hp ?? game.hp;
  const maxHp = display?.maxHp ?? getMaxHp(game);
  const shield = display?.shield ?? getPlayerShield(game);
  const healthScale = Math.max(maxHp, hp + shield);
  const healthChunks = Math.min(200, Math.ceil(healthScale));
  const plainHealth = usesPlainHealthBars();
  const shards = display?.shards ?? game.shards;
  const [healingArrival, setHealingArrival] = useState(null);
  const healingSequence = useRef(0);
  const healthPulse = plainHealth
    ? 0
    : display?.healthPulse || healingArrival?.id || 0;
  const healthAmount = display?.healthPulse
    ? display.healthAmount
    : healingArrival?.amount || 0;
  const maxHpAmount = display?.healthPulse
    ? display.maxHpAmount
    : healingArrival?.maxHp || 0;
  const shardPulse = display?.shardPulse || 0;
  const previousHealth = useRef({ hp, maxHp });
  useEffect(() => {
    const previous = previousHealth.current;
    previousHealth.current = { hp, maxHp };
    if (hp > previous.hp && !plainHealth) {
      healingSequence.current += 1;
      setHealingArrival({
        id: healingSequence.current,
        amount: hp - previous.hp,
        maxHp: Math.max(0, maxHp - previous.maxHp),
      });
      const finish = setTimeout(() => setHealingArrival(null), 900);
      return () => clearTimeout(finish);
    }
    setHealingArrival(null);
    return undefined;
  }, [hp, maxHp, reduceMotion, plainHealth]);
  const status = (
    <section
      className={`coreboundRunStatus ${cinematic ? 'is-collecting' : ''} ${
        reduceMotion ? 'is-reduced' : ''
      } ${hiddenOnMobile ? 'coreboundRunStatusSetup' : ''} ${
        boardActive ? 'is-board' : ''
      } ${inShop ? 'is-shop' : ''} ${hideStats ? 'is-health-only' : ''} ${
        hp <= maxHp * 0.25 ? 'is-low' : ''
      }`}
      aria-label={inShop ? 'Shop health' : 'Run status'}
    >
      <div className="coreboundRunHealth">
        <div className="coreboundStat">
          <span>Health</span>
          <strong>
            {hp} / {maxHp}
            <CoreShieldValue shield={shield} />
          </strong>
          {healthPulse > 0 && (
            <b
              key={`health-label:${healthPulse}`}
              className="coreboundHealthArrival"
              role="status"
              aria-live="polite"
            >
              <span>+{healthAmount} HP</span>
              {maxHpAmount > 0 && <small>+{maxHpAmount} MAX HP</small>}
            </b>
          )}
        </div>
        <div
          ref={healthRef}
          className={`coreboundHealth ${healthPulse ? 'has-repair' : ''}`}
          role="progressbar"
          aria-label="HP"
          aria-valuemin={0}
          aria-valuemax={maxHp}
          aria-valuenow={hp}
          aria-valuetext={
            shield > 0 ? `${hp} of ${maxHp} HP, ${shield} shield` : undefined
          }
        >
          <HealthLossFragments
            hp={hp}
            maxHp={healthScale}
            reduceMotion={reduceMotion}
          />
          <span
            className="coreboundHealthFill"
            style={{
              width: `${Math.max(0, Math.min(100, (hp / healthScale) * 100))}%`,
            }}
          >
            {!plainHealth && <i className="coreboundHealthFlow" />}
            {!plainHealth && (
              <i className="coreboundHealthCircuitry" aria-hidden="true">
                {Array.from({ length: Math.min(12, healthChunks) }, (_, index) => (
                  <i
                    key={index}
                    style={{
                      '--cell-delay': `${(index % 7) * -0.45}s`,
                      '--trace-height': `${index % 2 ? 38 : 62}%`,
                    }}
                  />
                ))}
              </i>
            )}
            {healthPulse > 0 && (
              <i
                key={`health-bar:${healthPulse}`}
                className="coreboundHealthReinforce"
              />
            )}
          </span>
          {!plainHealth && (
            <span
              className="coreboundHealthChunks"
              aria-hidden="true"
              style={{ '--health-chunks': healthChunks }}
            >
              {Array.from({ length: healthChunks }, (_, index) => (
                <i key={index} />
              ))}
            </span>
          )}
          <HealthShieldSection
            hp={hp}
            shield={shield}
            scale={healthScale}
            label="Player"
          />
        </div>
        {healthPulse > 0 && (
          <i
            key={`health-impact:${healthPulse}`}
            className="coreboundRewardImpact is-health"
          />
        )}
      </div>
      <div className="coreboundRunStats" hidden={hideStats}>
        {coreBoss ? (
          <div className="coreboundStat coreboundRunCoreStatus">
            <span>CORE · {getCoreAppearance(coreBoss.phase).name}</span>
            <strong>
              {coreBoss.hp} / {BOSS_MAX_HP} HP
              <CoreShieldValue shield={coreBoss.shield} />
            </strong>
            <div
              className="coreboundHealth coreboundBossHealth"
              role="progressbar"
              aria-label="Glitch Core aiming HP"
              aria-valuemin={0}
              aria-valuemax={BOSS_MAX_HP}
              aria-valuenow={coreBoss.hp}
            >
              <CoreHealthFill
                hp={coreBoss.hp}
                shield={coreBoss.shield}
                reduceMotion={reduceMotion}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="coreboundStat">
              <span>Sector</span>
              <strong>
                {game.floor} / {TOTAL_SECTORS}
              </strong>
            </div>
            <div className="coreboundStat coreboundShardStat">
              <span>{game.phase === 'won' ? 'Total shards' : 'Shards'}</span>
              <strong
                ref={shardsRef}
                key={`shard-count:${shardPulse}`}
                className={shardPulse ? 'coreboundShardCountArrival' : ''}
              >
                {game.phase === 'won' ? getTotalShardsAcquired(game) : shards}
              </strong>
              {shardPulse > 0 && (
                <i
                  key={`shard-impact:${shardPulse}`}
                  className="coreboundRewardImpact is-shards"
                />
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
  return cinematic ? createPortal(status, document.body) : status;
};

export default RunStatus;
